import type { AnyFieldApi } from "@tanstack/solid-form";
import {
  type Component,
  createMemo,
  createSignal,
  onMount,
  Show,
} from "solid-js";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAllCountriesWithDialCodes,
  getCountryByDialCode,
} from "@/constants/nationality-flags";

type PhoneInputProps = {
  fieldApi: AnyFieldApi;
  defaultCountryCode?: string;
  helpText?: string;
};

// Phone number validation schema
export const phoneNumberSchema = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val === "") return true; // Allow empty/optional
      // Check format: must start with +, followed by country code (1-9), then 1-14 digits
      if (!/^\+[1-9]\d{1,14}$/.test(val)) {
        return false;
      }
      // Check minimum length: total phone number should be at least 8 characters
      // (e.g., +1234567 = country code + minimum 6 digits)
      // This ensures we have a valid country code + at least 6 digits for the number
      return val.length >= 8;
    },
    { message: "Numero di telefono non valido" },
  );

type Country = {
  name: string;
  dialCode: string;
  isoCode: string;
  flag: string;
};

// Parse phone number to extract country code and number
const parsePhoneNumber = (phoneNumber: string | undefined | null) => {
  if (!phoneNumber) return { countryCode: "+39", number: "" };

  const allCountries = getAllCountriesWithDialCodes();
  // Try to find a matching country code at the start
  const sortedCountries = [...allCountries].sort(
    (a: Country, b: Country) => b.dialCode.length - a.dialCode.length,
  );

  for (const country of sortedCountries) {
    if (phoneNumber.startsWith(country.dialCode)) {
      return {
        countryCode: country.dialCode,
        number: phoneNumber.slice(country.dialCode.length).trim(),
      };
    }
  }

  // Default to Italy if no match found
  return { countryCode: "+39", number: phoneNumber };
};

// Combine country code and number
const combinePhoneNumber = (countryCode: string, number: string): string => {
  if (!number) return "";
  return `${countryCode}${number}`;
};

export const PhoneInput: Component<PhoneInputProps> = (props) => {
  const { t } = useI18n();
  const initialValue = props.fieldApi.state.value as string | undefined | null;
  const parsed = parsePhoneNumber(initialValue);

  const [countryCode, setCountryCode] = createSignal<string>(
    props.defaultCountryCode || parsed.countryCode,
  );
  const [phoneNumber, setPhoneNumber] = createSignal<string>(parsed.number);

  // Get country options sorted by dial code
  const countryOptions = createMemo(() => {
    const allCountries = getAllCountriesWithDialCodes();
    return [...allCountries].sort((a: Country, b: Country) => {
      // Sort by dial code length (longer first) then alphabetically
      if (a.dialCode.length !== b.dialCode.length) {
        return b.dialCode.length - a.dialCode.length;
      }
      return a.dialCode.localeCompare(b.dialCode);
    });
  });

  // Update field value when country code or phone number changes
  const updateFieldValue = (code: string, number: string) => {
    const combined = combinePhoneNumber(code, number);
    props.fieldApi.handleChange?.(combined || undefined);
  };

  const handleCountryCodeChange = (value: string | null) => {
    const code = value || "+39";
    setCountryCode(code);
    updateFieldValue(code, phoneNumber());
  };

  const handlePhoneNumberChange = (value: string) => {
    // Remove any non-digit characters except + at the start
    const cleaned = value.replace(/[^\d]/g, "");
    setPhoneNumber(cleaned);
    updateFieldValue(countryCode(), cleaned);
  };

  // Initialize field value on mount
  onMount(() => {
    if (initialValue) {
      updateFieldValue(countryCode(), phoneNumber());
    }
  });

  return (
    <div class="flex items-start gap-2">
      <div class="w-fit">
        <Select<string>
          options={countryOptions().map((c) => c.dialCode)}
          value={countryCode()}
          onChange={handleCountryCodeChange}
          onBlur={props.fieldApi.handleBlur}
          placeholder="Select country"
          itemComponent={(item) => {
            const country = getCountryByDialCode(item.item.rawValue);
            return (
              <SelectItem {...item}>
                <span class="flex items-center gap-2">
                  <span>{country?.flag || "🏳️"}</span>
                  <span>{item.item.rawValue}</span>
                </span>
              </SelectItem>
            );
          }}
        >
          <SelectTrigger class="w-fit min-w-[100px] bg-white">
            <SelectValue>
              <span class="flex items-center gap-1">
                <span>{getCountryByDialCode(countryCode())?.flag || "🏳️"}</span>
                <span>{countryCode()}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent class="max-h-[200px]" />
        </Select>
      </div>
      <div class="flex-1">
        <Input
          id={props.fieldApi.name}
          name={props.fieldApi.name}
          type="tel"
          value={phoneNumber()}
          onInput={(e) => handlePhoneNumberChange(e.target.value)}
          onBlur={() => props.fieldApi.handleBlur()}
          placeholder={
            (t("common.phone_number.placeholder") as string) || "Phone number"
          }
          class="w-full"
        />
        <Show
          when={props.helpText || props.fieldApi.state.meta.errors.length > 0}
        >
          <div class="mt-1">
            <Show
              when={props.fieldApi.state.meta.errors.length > 0}
              fallback={<p class="text-xs text-gray-500">{props.helpText}</p>}
            >
              <p class="text-xs text-red-500">
                {props.fieldApi.state.meta.errors[0] as string}
              </p>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
};
